import { ProfileView } from '@workspace/seedforge-features/profile'
import type {
  HeartbeatInstrumentation,
  ProfileTrackingConfig,
} from '@workspace/seedforge-features/public-profile'
import {
  investorPageUrl,
  PublicProfileView,
  useProfileTracker,
} from '@workspace/seedforge-features/public-profile'
import { formatDollars } from '@workspace/seedforge-shared'
import { Avatar, AvatarFallback, AvatarImage } from '@workspace/ui/components/avatar'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { FullScreenState } from '@workspace/ui/components/full-screen-state'
import { Input } from '@workspace/ui/components/input'
import { Skeleton } from '@workspace/ui/components/skeleton'
import { ToastProvider } from '@workspace/ui/components/toast'
import { useLocalStorage } from '@workspace/ui/hooks/use-storage'
import { getInitials } from '@workspace/ui/lib/utils'
import { ArrowRight, FileText, Lock, Mail, RadioTower, ShieldCheck } from 'lucide-react'
import type { ComponentProps, ReactNode } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { VideoPreviewModal } from '../../components/video-preview-modal'
import {
  fetchProfileChatHistory,
  openProfileChatStream,
  useContactFounderMutation,
  useCurrentUserQuery,
  useJoinAgentWaitlistMutation,
  useProfileChatContextQuery,
  useProfileHeartbeatMutation,
  useProfileTrackEventMutation,
  usePublicDocumentDownloadMutation,
  usePublicProfileFullQuery,
  usePublicProfilePreviewQuery,
  usePublicVideoUrlMutation,
  useRegisterProfileVisitorMutation,
} from '../../hooks/api'
import { useDocumentPreview } from '../../hooks/use-document-preview'
import { useVideoPreview } from '../../hooks/use-video-preview'
import { useSession } from '../../lib/auth'
import { trackEvent } from '../../lib/gtag'
import type { AskProfileDoorSubmission, ProfileChatQuestionInput } from '../ask-profile'
import { AskProfileProvider, AskProfileSection, DeckSlideRow, useAskProfile } from '../ask-profile'
import { FollowControl } from '../investor-assistant/components/relationship/follow-control'
import { useAssistantAccess } from '../investor-assistant/hooks/use-investor-assistant'
import { DocumentPreviewModal } from '../preview/components/document-preview-modal'
import { InlineDocumentPreview } from '../preview/components/inline-document-preview'

/** What the app puts around the profile: the bar above it and the column beside it. */
export interface AppProfileChrome {
  /** The signed-in investor's account email: a link that asks for an address is opened with it. */
  viewerEmail: string | null
  render: (
    startup: { id: string; name: string },
    /** The profile, given the buttons that sit beside its title. */
    profile: (titleActions: ReactNode) => ReactNode,
    /** Asks the founder for an intro with the investor's message; the profile itself shows no such button here. */
    intro: {
      send: (message: string) => void
      pending: boolean
      status: 'idle' | 'pending' | 'success' | 'error'
    }
  ) => ReactNode
}

/**
 * A startup's shared profile, behind the link the founder sent. Public on /s/<token>; in the app
 * the same page (and the same visit, which the founder sees as a signed-in investor's) sits in the
 * investor layout with `app` supplying the chrome around the profile.
 */
export function SharedProfilePage(props: { token: string; app?: AppProfileChrome }) {
  return (
    <ToastProvider>
      <SharedProfile {...props} />
    </ToastProvider>
  )
}

function SharedProfile({ token, app }: { token: string; app?: AppProfileChrome }) {
  const previewQuery = usePublicProfilePreviewQuery(token)
  const registerVisitor = useRegisterProfileVisitorMutation()

  // Persisted across tabs, not just this one. The gate itself is counted on the
  // server and does not depend on this, but a token that died with the tab
  // would hand a returning visitor a fresh anonymous identity — so someone who
  // already gave their email would be asked again on a gated link, and every
  // tab would open a new visitor in the founder's analytics.
  const [visitorToken, setVisitorToken] = useLocalStorage(`sf-visitor:${token}`)
  const [visit, setVisit] = useState<VisitState | null>(null)
  const [visitFailed, setVisitFailed] = useState(false)
  // Set when the visitor triggers something that needs an email before the gate
  // view would have asked for one (contacting the founder).
  const [emailDemanded, setEmailDemanded] = useState(false)
  // Start the visit exactly once per mount: resume the visitor this device
  // already has, or open an anonymous one, and learn whether this view is gated.
  const startedRef = useRef(false)
  useEffect(() => {
    if (startedRef.current) return
    startedRef.current = true
    // mutateAsync, not mutate: mutate's callbacks are bound to the observer and
    // are dropped if it unmounts before the request settles, which StrictMode's
    // remount does on every mount in dev. The ref guard then blocks the retry
    // and the page waits on a visit that never arrives.
    registerVisitor
      .mutateAsync({ path: { token }, body: { visitorToken: visitorToken ?? undefined } })
      .then((response) => {
        setVisitorToken(response.data.visitorToken)
        setVisit(response.data)
      })
      .catch(() => setVisitFailed(true))
  }, [token, visitorToken, registerVisitor, setVisitorToken])

  // In the app the investor is signed in: a link that asks for an address is opened with the one on
  // their account instead of asking them.
  const autoEmailRef = useRef(false)
  const viewerEmail = app?.viewerEmail ?? null
  useEffect(() => {
    if (!visit?.emailRequired || !viewerEmail || autoEmailRef.current) return
    autoEmailRef.current = true
    registerVisitor
      .mutateAsync({
        path: { token },
        body: { email: viewerEmail, visitorToken: visit.visitorToken },
      })
      .then((response) => {
        setVisitorToken(response.data.visitorToken)
        setVisit(response.data)
      })
      .catch(() => setVisitFailed(true))
  }, [visit, viewerEmail, token, registerVisitor, setVisitorToken])

  const handleRegistered = useCallback(
    (registered: VisitState) => {
      setVisitorToken(registered.visitorToken)
      setVisit(registered)
      setEmailDemanded(false)
    },
    [setVisitorToken]
  )

  // Loading must be checked first: `!previewQuery.data` is also true while
  // the query is still in flight, not only once it has actually failed — the
  // reverse order flashed "Link not found" on every fresh page load before
  // the query had a chance to resolve.
  if (previewQuery.isLoading || (!previewQuery.data && !previewQuery.isError)) {
    return <FullScreenState loading title="Loading..." />
  }

  if (previewQuery.isError || !previewQuery.data || visitFailed) {
    return (
      <FullScreenState
        title="Link not found"
        subtitle="This share link may have been revoked or does not exist."
      />
    )
  }

  if (!visit) {
    return <FullScreenState loading title="Loading..." />
  }

  if ((visit.emailRequired && !viewerEmail) || emailDemanded) {
    return (
      <EmailGate
        startupName={previewQuery.data.data.startupName}
        startupInitials={previewQuery.data.data.startupInitials}
        founderName={previewQuery.data.data.founderName}
        founderImageUrl={previewQuery.data.data.founderImageUrl}
        token={token}
        visitorToken={visit.visitorToken}
        shareLinkId={visit.shareLinkId}
        onRegistered={handleRegistered}
      />
    )
  }

  return (
    <FullProfileView
      token={token}
      visitorToken={visit.visitorToken}
      shareLinkId={visit.shareLinkId}
      hasEmail={visit.hasEmail}
      app={app}
      onRequireEmail={() => setEmailDemanded(true)}
    />
  )
}

type VisitState = {
  visitorToken: string
  hasEmail: boolean
  emailRequired: boolean
  shareLinkId: string
}

const VIEWER_EMAIL_KEY = 'sf-viewer-email'
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** The address this device has already given, on any /s/ link. */
function readSavedViewerEmail(): string {
  try {
    return window.localStorage.getItem(VIEWER_EMAIL_KEY) ?? ''
  } catch {
    // localStorage unavailable (e.g. private browsing) — ask for it again.
    return ''
  }
}

function EmailGate({
  startupName,
  startupInitials,
  founderName,
  founderImageUrl,
  token,
  visitorToken,
  shareLinkId,
  onRegistered,
}: {
  startupName: string
  startupInitials: string
  founderName: string | null
  founderImageUrl: string | null
  token: string
  visitorToken: string
  shareLinkId: string
  onRegistered: (visit: VisitState) => void
}) {
  // The gate owns its mutation rather than sharing the page's check-in one:
  // this button's pending/error state should reflect this submit only.
  const registerVisitor = useRegisterProfileVisitorMutation()
  const founderFirstName = founderName?.trim().split(/\s+/)[0] ?? null
  const [email, setEmail] = useState('')
  const [touched, setTouched] = useState(false)
  const emailInputRef = useRef<HTMLInputElement>(null)

  const trimmedEmail = email.trim()
  const isValidEmail = EMAIL_PATTERN.test(trimmedEmail)
  const showValidationError = touched && trimmedEmail.length > 0 && !isValidEmail

  // Remember the viewer across all /s/ share links on this device, so a repeat visitor never re-types.
  useEffect(() => {
    const saved = readSavedViewerEmail()
    if (saved) setEmail(saved)
    emailInputRef.current?.focus()
  }, [])

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setTouched(true)
    if (!isValidEmail) {
      emailInputRef.current?.focus()
      return
    }
    const trimmed = trimmedEmail
    registerVisitor.mutate(
      {
        path: { token },
        // Carry the visitor token so the email lands on the visitor who already
        // took the earlier views, keeping them on one timeline.
        body: { email: trimmed, visitorToken },
      },
      {
        onSuccess: (response) => {
          try {
            window.localStorage.setItem(VIEWER_EMAIL_KEY, trimmed)
          } catch {
            // ignore storage failures
          }
          onRegistered(response.data)
        },
      }
    )
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background p-5">
      <ProfilePreviewBackdrop startupName={startupName} startupInitials={startupInitials} />

      <div className="relative z-10 w-full max-w-[27rem] rounded-2xl border border-border bg-card/90 p-6 shadow-2xl backdrop-blur-xl">
        <div className="mb-4 flex items-center gap-3 border-b border-border pb-4">
          {founderFirstName ? (
            <>
              <Avatar className="size-11 flex-none border border-border">
                {founderImageUrl && <AvatarImage src={founderImageUrl} alt={founderName ?? ''} />}
                <AvatarFallback className="bg-primary/10 text-base font-bold text-primary">
                  {getInitials(founderName ?? startupName)}
                </AvatarFallback>
              </Avatar>
              <div className="text-[15px] leading-snug">
                <span className="font-bold text-foreground">{founderName}</span>{' '}
                <span className="text-muted-foreground">shared this profile with you</span>
              </div>
            </>
          ) : (
            <>
              <span className="flex size-11 flex-none items-center justify-center rounded-xl border border-primary/40 bg-primary/10 text-base font-bold text-primary">
                {startupInitials}
              </span>
              <div>
                <div className="text-[17px] font-bold leading-tight text-foreground">
                  {startupName}
                </div>
              </div>
            </>
          )}
        </div>

        <h1 className="mb-4 text-xl font-bold tracking-tight text-foreground">
          View {startupName}'s profile
        </h1>

        <div className="mb-5 rounded-xl border border-border bg-muted/40 px-4 py-3">
          <div className="mb-2.5 text-[10.5px] font-bold uppercase tracking-wider text-muted-foreground">
            What's inside
          </div>
          <div className="flex items-center gap-2.5 py-1 text-[13px] text-muted-foreground">
            <FileText className="size-4 flex-none text-primary" />
            The pitch deck
          </div>
          <div className="flex items-center gap-2.5 py-1 text-[13px] text-muted-foreground">
            <ArrowRight className="size-4 flex-none text-primary" />A structured, always-updated
            read of the business
          </div>
          <div className="flex items-center gap-2.5 py-1 text-[13px] text-muted-foreground">
            <RadioTower className="size-4 flex-none text-primary" />
            The founder's recorded walkthrough and live metrics
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-1.5">
          <label
            htmlFor="gate-email"
            className="mb-1.5 block text-[11px] font-bold uppercase tracking-wide text-foreground"
          >
            Your email
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-foreground" />
            <Input
              ref={emailInputRef}
              id="gate-email"
              type="email"
              placeholder="you@fund.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setTouched(true)}
              className={`h-11 border border-primary/70 pl-10 font-medium text-foreground ${showValidationError ? 'border-destructive focus-visible:ring-destructive' : ''}`}
              aria-invalid={showValidationError}
              autoFocus
              required
            />
          </div>
          {showValidationError && (
            <p className="text-[11.5px] text-destructive">Enter a valid email address.</p>
          )}
          <p className="mb-1 mt-2 text-[11.5px] leading-relaxed text-muted-foreground">
            So {startupName} knows you stopped by. We will not email you, and we never share it.
          </p>
          <CtaButton
            type="submit"
            className="mt-2"
            fullWidth
            arrow
            loading={registerVisitor.isPending}
            disabled={!trimmedEmail}
          >
            {registerVisitor.isPending ? 'Loading...' : 'View profile'}
          </CtaButton>
          {registerVisitor.isError && (
            <p className="text-center text-xs text-destructive">
              Something went wrong. Please try again.
            </p>
          )}
        </form>

        <div className="mt-4 grid gap-2.5 border-t border-border pt-4">
          <div className="flex items-start gap-2.5 text-[12.5px] leading-relaxed text-muted-foreground">
            <span className="flex size-6 flex-none items-center justify-center rounded-md bg-primary/10">
              <Lock className="size-3.5 text-primary" />
            </span>
            <span>
              <span className="font-semibold text-foreground">Private and secure.</span> Only{' '}
              {startupName} sees who viewed. Nothing here is public.
            </span>
          </div>
          <div className="flex items-start gap-2.5 text-[12.5px] leading-relaxed text-muted-foreground">
            <span className="flex size-6 flex-none items-center justify-center rounded-md bg-primary/10">
              <ShieldCheck className="size-3.5 text-primary" />
            </span>
            <span>
              <span className="font-semibold text-foreground">Enter once.</span> Remembered on this
              device, so you skip this next time.
            </span>
          </div>
        </div>

        <a
          href={investorPageUrl(shareLinkId)}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 flex items-center justify-center gap-1.5 border-t border-border pt-3 text-center text-[10px] text-muted-foreground transition-opacity hover:opacity-80"
        >
          Powered by
          <img src="/seedforge-full-light.svg" alt="SeedForge" className="h-3.5" />
        </a>
      </div>
    </div>
  )
}

function ProfilePreviewBackdrop({
  startupName,
  startupInitials,
}: {
  startupName: string
  startupInitials: string
}) {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 select-none px-8 py-8 opacity-40 blur-md"
    >
      <div className="mb-8 flex items-center gap-3 border-b border-border pb-4">
        <span className="flex size-6 flex-none items-center justify-center rounded-md bg-primary/20 text-[11px] font-bold text-primary">
          {startupInitials}
        </span>
        <span className="text-sm font-bold text-foreground">{startupName}</span>
        <div className="ml-4 hidden gap-5 text-xs text-muted-foreground sm:flex">
          <span>Overview</span>
          <span>Traction</span>
          <span>Team</span>
          <span>Market</span>
        </div>
      </div>
      <div className="mb-2 h-9 w-2/3 max-w-md rounded bg-muted" />
      <div className="mb-8 h-4 w-1/2 max-w-sm rounded bg-muted" />
      <div className="grid grid-cols-2 gap-3.5 sm:grid-cols-3">
        {['Traction', 'Team', 'Market', 'What investors asked', 'Walkthrough', 'Financials'].map(
          (label) => (
            <div key={label} className="h-32 rounded-xl border border-border bg-card p-4">
              <div className="mb-3 text-xs font-semibold text-foreground">{label}</div>
              <Skeleton className="mb-2 h-2 w-4/5" />
              <Skeleton className="mb-2 h-2 w-3/5" />
              <Skeleton className="h-2 w-2/3" />
            </div>
          )
        )}
      </div>
    </div>
  )
}

/**
 * `openDoor` lives on the ask-profile context, which only exists BELOW the
 * provider — and the profile view is the provider's child, so the route cannot
 * read it at the call site. This wrapper exists purely to cross that boundary,
 * and to keep the door taxonomy in one place: the post-gate card's "Follow this
 * company" is the same agent door as the CTA bar's, tagged with its own
 * placement so the read-out can tell them apart.
 */
function ProfileViewWithDoors({
  token,
  startupId,
  app,
  connectStatus,
  ...props
}: ComponentProps<typeof PublicProfileView> & {
  token: string
  startupId: string
  app?: AppProfileChrome
  connectStatus?: 'idle' | 'pending' | 'success' | 'error'
}) {
  const { openDoor } = useAskProfile()
  // The Follow control belongs to the assistant's investors; others keep the waitlist door.
  const canFollow = useCanFollow()

  // In the app the profile sits inside the investor layout, beside the startup's cards.
  if (app) {
    const { onConnect, connectPending, ...profileProps } = props
    return (
      <>
        {app.render(
          { id: startupId, name: props.startupName ?? '' },
          (titleActions) => (
            <ProfileView {...profileProps} isPublic titleActions={titleActions} />
          ),
          {
            send: (message) => onConnect?.(message),
            pending: connectPending ?? false,
            status: connectStatus ?? 'idle',
          }
        )}
      </>
    )
  }

  // A signed-in investor follows from the control in the header; anyone else leaves their address
  // on the waitlist door.
  const handleFollow = () => {
    trackEvent('share_follow_clicked', { signed_in_investor: false })
    openDoor('agent_follow', 'post_gate')
  }

  return (
    <PublicProfileView
      {...props}
      headerActions={
        canFollow ? (
          <FollowControl
            startupId={startupId}
            startupName={props.startupName ?? ''}
            shareToken={token}
          />
        ) : undefined
      }
      onFollowCompany={canFollow ? undefined : handleFollow}
    />
  )
}

/** Whether this viewer follows from the Follow control in the profile: an investor with the assistant. */
function useCanFollow(): boolean {
  const { data: session } = useSession()
  const { data: me } = useCurrentUserQuery({ enabled: !!session })
  return useAssistantAccess(me?.userType === 'INVESTOR')
}

function FullProfileView({
  token,
  visitorToken,
  shareLinkId,
  hasEmail,
  app,
  onRequireEmail,
}: {
  token: string
  visitorToken: string
  shareLinkId: string
  hasEmail: boolean
  app?: AppProfileChrome
  onRequireEmail: () => void
}) {
  const canFollow = useCanFollow()
  const profileQuery = usePublicProfileFullQuery(token, visitorToken)
  const heartbeatMutation = useProfileHeartbeatMutation()
  const trackEventMutation = useProfileTrackEventMutation()
  const documentDownloadMutation = usePublicDocumentDownloadMutation()
  const contactFounderMutation = useContactFounderMutation()
  const startupId = profileQuery.data?.data?.startupId

  const handleRequestDiligence = useCallback(() => {
    if (!startupId) return
    window.location.href = `/app/evaluate/${startupId}`
  }, [startupId])

  // Document page tracking state
  const [docPage, setDocPage] = useState<{ page: number; totalPages: number } | null>(null)
  const docPageRef = useRef(docPage)
  docPageRef.current = docPage

  // The inline deck's own page, kept apart from `docPage`: that one also moves
  // when the modal previewer pages through a different document, and "Ask about
  // slide N" must name the slide on screen behind the chat, not the last page
  // turned anywhere.
  const [deckSlide, setDeckSlide] = useState<{ page: number; totalPages: number } | null>(null)

  const handleDownloadDeck = useCallback(
    async (documentId: string) => {
      const res = await documentDownloadMutation.mutateAsync({
        path: { token, documentId },
        body: { attachment: true, visitorToken },
      })
      window.open(res.data.downloadUrl, '_blank', 'noopener,noreferrer')
      trackEventMutation.mutate({
        path: { token },
        body: { visitorToken, type: 'document_download', resourceId: documentId },
      })
    },
    [token, visitorToken, documentDownloadMutation, trackEventMutation]
  )

  // ─── Ask the profile ─────────────────────────────────────────────────────
  // The endpoint 404s when the flag or the founder's own switch is off, so a
  // failed context call is the widget's off switch and nothing renders.
  const chatContextQuery = useProfileChatContextQuery(token, visitorToken)
  const chatContext = chatContextQuery.data ?? null
  const joinAgentWaitlistMutation = useJoinAgentWaitlistMutation()

  const handleAskProfileEvent = useCallback(
    (type: string, resourceId?: string, metadata?: Record<string, unknown>) => {
      trackEventMutation.mutate({
        path: { token },
        body: { visitorToken, type: type as 'pitch_play', resourceId, metadata },
      })
    },
    [token, visitorToken, trackEventMutation]
  )

  // The anonymous transport: a visitor token in the body, and every turn
  // recorded. The founder preview supplies its own authenticated one instead.
  const openChatStream = useCallback(
    (input: ProfileChatQuestionInput, signal: AbortSignal) =>
      openProfileChatStream(token, { ...input, visitorToken }, signal),
    [token, visitorToken]
  )

  // A reload's own read: the controller calls this once, on mount, only when
  // this device already has a chat session id in storage to ask about.
  const fetchChatHistory = useCallback(
    (chatSessionId: string) => fetchProfileChatHistory(token, { visitorToken, chatSessionId }),
    [token, visitorToken]
  )

  const handleAskProfileDoorSubmit = useCallback(
    (submission: AskProfileDoorSubmission) => {
      // The whole submission, not just the email. The two doors exist to produce
      // distinguishable signals, and what distinguishes them is the FIELD each
      // captures — so a call carrying only the address would be, for
      // measurement, one door. The server back-fills the email onto the visitor
      // row itself, so this one call replaces the register call that used to be
      // here rather than joining it.
      joinAgentWaitlistMutation.mutate({
        path: { token },
        body: {
          visitorToken,
          email: submission.email,
          intent: submission.door === 'agent_follow' ? 'AGENT_FOLLOW' : 'THESIS_FIT',
          placement: submission.placement,
          brief: submission.brief,
        },
      })
    },
    [token, visitorToken, joinAgentWaitlistMutation]
  )

  // The connect dialog is owned by `BottomCta`'s local state and cannot be
  // opened from here, so the chat's founder actions take the visitor to it
  // rather than reaching into it.
  const handleAskProfileConnect = useCallback(() => {
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })
  }, [])

  // Shared by the inline deck and the modal previewer — both report page turns
  // the same way, only the document they are showing differs.
  const handleDocumentPageChange = useCallback(
    (documentId: string | undefined, page: number, totalPages: number) => {
      const prev = docPageRef.current
      setDocPage({ page, totalPages })
      trackEventMutation.mutate({
        path: { token },
        body: {
          visitorToken,
          type: 'document_page_change' as 'pitch_play',
          resourceId: documentId,
          metadata: { fromPage: prev?.page ?? null, toPage: page, totalPages },
        },
      })
    },
    [token, visitorToken, trackEventMutation]
  )

  // Document preview state — also pauses heartbeats while viewing
  const docPreview = useDocumentPreview({
    fetchDocumentUrl: async (documentId) => {
      const res = await documentDownloadMutation.mutateAsync({
        path: { token, documentId },
        body: { visitorToken },
      })
      return res.data.downloadUrl
    },
  })

  // Video preview state
  const videoUrlMutation = usePublicVideoUrlMutation()
  const video = useVideoPreview({
    fetchId: token,
    fetchVideoUrl: (t) =>
      videoUrlMutation
        .mutateAsync({ path: { token: t }, body: { visitorToken } })
        .then((r) => r.data.downloadUrl),
  })

  const tracking: ProfileTrackingConfig = useMemo(
    () => ({
      onHeartbeat: (
        section: string,
        resourceId?: string,
        page?: number,
        totalPages?: number,
        instrumentation?: HeartbeatInstrumentation
      ) => {
        heartbeatMutation.mutate({
          path: { token },
          body: {
            visitorToken,
            section: section as 'profile' | 'document' | 'pitch',
            resourceId,
            page,
            totalPages,
            ...instrumentation,
            evaluationId: profileQuery.data?.data?.evaluation?.id,
          },
        })
      },
      onEvent: (type: string, resourceId?: string, metadata?: Record<string, unknown>) => {
        trackEventMutation.mutate({
          path: { token },
          body: { visitorToken, type: type as 'pitch_play', resourceId, metadata },
        })
      },
      onDocumentOpen: (documentId: string) => {
        const doc = profileQuery.data?.data?.documents.find((d) => d.id === documentId)
        if (doc) {
          docPreview.openDocument({ id: doc.id, fileName: doc.fileName, mimeType: doc.mimeType })
        }
        return docPreview.fetchDocumentUrl(documentId)
      },
      activeDocument: docPreview.previewOpen ? docPreview.previewDoc?.id : undefined,
      activeVideo: video.videoOpen,
      // Only while the modal is open — pitchSessionId alone is truthy for the
      // whole page life and would route every heartbeat to the pitch bucket.
      activeVideoId: video.videoOpen
        ? (profileQuery.data?.data?.pitchSessionId ?? undefined)
        : undefined,
      documentPage: docPageRef.current ?? undefined,
      // `pitchDeck` itself isn't declared until after this component's
      // loading/error guards (below), which must come after every hook — so
      // this mirrors the same defensive lookup those guards would otherwise
      // block, rather than referencing that later binding.
      deckDocumentId: profileQuery.data?.data?.documents.find(
        (d) => d.type === 'PITCH_DECK' && d.mimeType === 'application/pdf'
      )?.id,
    }),
    [
      token,
      visitorToken,
      heartbeatMutation,
      trackEventMutation,
      profileQuery.data?.data?.documents,
      profileQuery.data?.data?.evaluation?.id,
      profileQuery.data?.data?.pitchSessionId,
      docPreview.openDocument,
      docPreview.fetchDocumentUrl,
      docPreview.previewOpen,
      docPreview.previewDoc?.id,
      video.videoOpen,
    ]
  )

  const { setRef: containerRef } = useProfileTracker(tracking)

  if (profileQuery.isLoading) {
    return <FullScreenState loading title="Loading profile..." />
  }

  if (profileQuery.isError || !profileQuery.data?.data) {
    return <FullScreenState title="Could not load profile" subtitle="Please try again later." />
  }

  const profile = profileQuery.data.data

  const { startup } = profile

  // Only PDFs render inline. An image or an unsupported type keeps the old
  // click-to-open CTA, which handles every type through the modal.
  const pitchDeck = profile.documents.find(
    (d) => d.type === 'PITCH_DECK' && d.mimeType === 'application/pdf'
  )

  return (
    <div ref={containerRef}>
      {/* Above PublicProfileView because the surface is split: the widget sits
          between the deck and the description, the deck-end card inside the
          deck frame, and both drive the same conversation. */}
      <AskProfileProvider
        openStream={openChatStream}
        startupName={startup.name}
        initialEmail={readSavedViewerEmail()}
        sources={chatContext?.sources ?? []}
        slideCount={chatContext?.slideCount ?? 0}
        documentCount={chatContext?.documentCount ?? 0}
        onDoorSubmit={handleAskProfileDoorSubmit}
        onConnectFounder={handleAskProfileConnect}
        onTrackEvent={handleAskProfileEvent}
        historyKey={token}
        fetchHistory={fetchChatHistory}
      >
        <ProfileViewWithDoors
          shareLinkId={shareLinkId}
          token={token}
          startupId={profile.startupId}
          app={app}
          connectStatus={contactFounderMutation.status}
          profile={{
            initials: startup.initials,
            name: startup.name,
            sector: '',
            sectorLabel: startup.categoryName ?? undefined,
            stage: startup.productStage ?? '',
            raisingStatus: startup.isFundraising,
            location: startup.location ?? '',
            founded: startup.foundingDate ?? '',
            website: startup.websiteUrl ?? '',
            description: startup.description ?? '',
            tags: startup.tags,
          }}
          fundraising={{
            round: startup.fundraisingStage ?? '',
            target: startup.fundraisingAmount
              ? formatDollars(Number(startup.fundraisingAmount))
              : '',
          }}
          pitchRecordings={
            profile.evaluation && profile.pitchSessionId
              ? [
                  {
                    id: profile.pitchSessionId,
                    duration: profile.evaluation.pitchDuration || '30 min',
                    readinessLabel: '',
                    recordedDate: String(profile.evaluation.date),
                    verdictSnippet: '',
                    highlights: [],
                    judgeCount: 0,
                    topStrength: '',
                    topConcern: '',
                  },
                ]
              : []
          }
          selectedPitchId={profile.pitchSessionId ?? ''}
          updates={profile.updates.map((u) => ({
            id: u.id,
            date: String(u.date),
            text: u.text,
          }))}
          documents={profile.documents.map((d) => ({
            name: d.fileName,
            size: d.fileSize != null ? `${(d.fileSize / 1024).toFixed(0)} KB` : '',
            documentId: d.id,
            type: d.type ?? null,
            uploadStatus: 'ready' as const,
          }))}
          team={profile.teamMembers.map((m) => ({
            id: m.id,
            initials: getInitials(m.name),
            name: m.name,
            role: m.position ?? '',
            linkedInUrl: m.linkedinUrl ?? undefined,
            imageUrl: m.imageUrl,
            companyLogos: m.companyLogos,
            lastEducation: m.lastEducation,
            linkedinScrapeStatus: m.linkedinScrapeStatus,
          }))}
          tracking={tracking}
          startupName={startup.name}
          onRequestDiligence={handleRequestDiligence}
          onConnect={(message) => {
            // The founder replies to the visitor's address, so an anonymous
            // visitor has to clear the gate first — the backend rejects the
            // request otherwise.
            if (!hasEmail) {
              onRequireEmail()
              return
            }
            contactFounderMutation.mutate({
              path: { token },
              body: { visitorToken, message },
            })
            trackEventMutation.mutate({
              path: { token },
              body: { visitorToken, type: 'contact_founder' as const, resourceId: 'connect' },
            })
          }}
          connectPending={contactFounderMutation.isPending}
          videoUrl={video.videoUrl ?? undefined}
          onPlayVideo={video.handlePlayRecording}
          deckPreview={
            pitchDeck ? (
              <>
                <InlineDocumentPreview
                  documentId={pitchDeck.id}
                  fetchUrl={docPreview.fetchDocumentUrl}
                  hotkeysEnabled={!docPreview.previewOpen && !video.videoOpen}
                  onPageChange={(page, totalPages) => {
                    setDeckSlide({ page, totalPages })
                    handleDocumentPageChange(pitchDeck.id, page, totalPages)
                  }}
                  placeholderImageUrls={pitchDeck.slideImageUrls}
                  initialAspectRatio={pitchDeck.deckAspectRatio}
                />
                {profile.allowDeckDownload && (
                  <button
                    type="button"
                    onClick={() => handleDownloadDeck(pitchDeck.id)}
                    className="mt-2 text-xs font-semibold text-primary hover:underline"
                  >
                    Download PDF
                  </button>
                )}
                {chatContext && deckSlide && (
                  <DeckSlideRow
                    page={deckSlide.page}
                    totalPages={deckSlide.totalPages}
                    documentId={pitchDeck.id}
                    onWatchPitch={profile.pitchSessionId ? video.handlePlayRecording : undefined}
                  />
                )}
              </>
            ) : undefined
          }
          chatWidget={chatContext ? <AskProfileSection doors={!canFollow} /> : undefined}
          hideBottomCta={canFollow}
        />
      </AskProfileProvider>
      {video.videoUrl && (
        <VideoPreviewModal
          videoUrl={video.videoUrl}
          title="Pitch Recording"
          open={video.videoOpen}
          onOpenChange={video.setVideoOpen}
          startTime={video.videoStartTime}
          onTimeUpdate={video.setVideoResumeTime}
        />
      )}
      <DocumentPreviewModal
        document={docPreview.previewDoc}
        open={docPreview.previewOpen}
        onOpenChange={docPreview.setPreviewOpen}
        fetchUrl={docPreview.fetchDocumentUrl}
        onPageChange={(page, totalPages) =>
          handleDocumentPageChange(docPreview.previewDoc?.id, page, totalPages)
        }
      />
    </div>
  )
}
